import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ImportErrorsModalFooter from 'components/ImportErrorsModalFooter/ImportErrorsModalFooter';
import Separator from 'components/Separator/Separator';
import { PRODUCT_SUPPLIERS_SOURCE, PROFIT_CENTRES_SOURCE } from 'constants/dataSets';
import { useAppDispatch } from 'store/hooks/useApp';
import { selfDataSets } from 'store/slices/dataSets/selectors';
import { setDataSetsState } from 'store/slices/dataSets/slice';
import checkHasDuplicateError from 'utils/checkDuplicateErrors';

import i18n from '../../i18n';
import Loader from '../Loader/Loader';
import Backdrop from '../Modal/Backdrop';
import ImportMultipleErrorsItem from './components/ImportMultipleErrorsItem';

import type { TUseProfitCenterImportRecordsMutation } from 'pages/UserPages/ProfitCentres/interfaces/TUseProfitCenterImportRecordsMutation';
import type { IDataMap } from 'store/slices/dataSets/interfaces/IDataMap';
import type IProfitCenterImportRecordsRequest from 'store/slices/dataSets/interfaces/IProfitCenterImportRecordsRequest';
import type IImportErrorsModal from './interfaces/IImportErrorsModal';

import styles from './scss/ImportMultipleErrorsModal.module.scss';

function ImportMultipleErrorsModal<T extends TUseProfitCenterImportRecordsMutation>({
  isImportErrorsModalVisible,
  setIsImportErrorsModalVisible,
  isLoading,
  importRecords,
  onImportError,
}: IImportErrorsModal<T>) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { mappingColumns } = useSelector(selfDataSets);
  const { sheetErrors } = mappingColumns || {};

  const [showButtons, setShowButtons] = useState(false);
  useEffect(() => {
    if (isImportErrorsModalVisible) {
      document.body.style.overflow = 'hidden';
    } else if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = '';
    }
  }, [isImportErrorsModalVisible]);

  const errorsKeys = sheetErrors && Object.keys(sheetErrors).length > 0 ? Object.keys(sheetErrors) : [];

  useEffect(() => {
    if (sheetErrors) {
      const hasDuplicateError = checkHasDuplicateError(sheetErrors);
      setShowButtons(hasDuplicateError);
    } else {
      setShowButtons(false);
    }
  }, [sheetErrors]);

  const onMappingModalSubmit = (type: 'error' | 'replace' | 'ignore') => {
    if (mappingColumns) {
      const requestData: IProfitCenterImportRecordsRequest = {
        url: mappingColumns?.url || '',
        dryRun: false,
        onPkDuplicate: type,
        Main: {
          source: mappingColumns.map[0].source,
          map: mappingColumns.map[0].map as IDataMap[],
        },
        [PROFIT_CENTRES_SOURCE]: {
          source: mappingColumns.map[1].source,
          map: mappingColumns.map[1].map as IDataMap[],
        },
      };

      if (mappingColumns.map[2]) {
        requestData[PRODUCT_SUPPLIERS_SOURCE] = {
          source: mappingColumns.map[2].source,
          map: mappingColumns.map[2].map as IDataMap[],
        };
      }

      importRecords(requestData).unwrap().then((importRecordsResponse) => {
        dispatch(setDataSetsState({ mappingColumns: importRecordsResponse.data }));
        if (importRecordsResponse?.data?.sheetErrors) {
          dispatch(setDataSetsState({ mappingColumns: importRecordsResponse.data }));
          setIsImportErrorsModalVisible(true);
        }
        setIsImportErrorsModalVisible(false);
      }).catch((error: unknown) => {
        if (onImportError) {
          setIsImportErrorsModalVisible(false);
          onImportError(error);
          return;
        }

        toast.error(i18n.t('notifications.somethingWentWrong'));
      });
    }
  };

  return isImportErrorsModalVisible ? createPortal(
    <Backdrop>
      <Loader isPageLoading={isLoading} />
      <div className={styles.root}>
        <h2 className={styles.title}>{t('dataSetsManagement.importErrorsModal.title')}</h2>
        <div className={styles.content}>
          <Separator />
          <div className={styles.wrapper}>
            {errorsKeys.map((key) => (
              <ImportMultipleErrorsItem
                key={key}
                sheetKey={key}
                sheetErrors={sheetErrors || {}}
              />
            ))}
          </div>
        </div>
        <ImportErrorsModalFooter
          showButtons={showButtons}
          onClose={() => { setIsImportErrorsModalVisible(false); }}
          onReplaceDuplicates={() => { onMappingModalSubmit('replace'); }}
          onSkipDuplicates={() => { onMappingModalSubmit('ignore'); }}
        />
      </div>
    </Backdrop>,
    document.body,
  ) : null;
}

export default ImportMultipleErrorsModal;
